import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API_BASE } from '../App';

function timeAgo(timestamp) {
  const diff = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
  if (diff < 60)    return 'just now';
  if (diff < 3600)  return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  return `${Math.floor(diff / 86400)} days ago`;
}

function ActivityLog({ user }) {
  const isSuper = user?.role === 'superadmin';
  const hasActivityPerm = isSuper || !!((user?.permissions || {}).activity);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [clearModal, setClearModal] = useState(false);
  const [clearing, setClearing] = useState(false);

  const loadActivity = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE}/api/activity`, { withCredentials: true });
      setActivity(res.data);
    } catch (err) {
      setError('Failed to load activity log.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadActivity(); }, [loadActivity]);

  const handleClearConfirm = async () => {
    setClearing(true);
    try {
      await axios.post(`${API_BASE}/api/activity/clear`, {}, { withCredentials: true });
      setClearModal(false);
      setActivity([]);
    } catch (err) {
      setError('Failed to clear activity log.');
      setClearModal(false);
    } finally {
      setClearing(false);
    }
  };

  if (!hasActivityPerm) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', flexDirection: 'column', gap: '16px' }}>
        <div style={{ fontSize: '48px' }}>🔒</div>
        <div style={{ fontSize: '14px', color: '#AAB0B8', fontFamily: 'Manrope, sans-serif' }}>You don't have permission to view the activity log.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', flexDirection: 'column', gap: '16px' }}>
        <div style={{ width: '36px', height: '36px', borderRadius: '50%', border: '3px solid #F4FAFF', borderTop: '3px solid #F1FF5C', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ fontSize: '13px', color: '#AAB0B8', fontFamily: 'Manrope, sans-serif' }}>Loading activity…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', flexDirection: 'column', gap: '16px' }}>
        <div style={{ fontSize: '14px', color: '#FF766B', fontFamily: 'Manrope, sans-serif' }}>{error}</div>
        <button type="button" onClick={loadActivity} style={{ padding: '10px 24px', borderRadius: '8px', border: 'none', background: '#F1FF5C', color: '#22272D', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'Manrope, sans-serif' }}>Retry</button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontFamily: 'Sora, sans-serif', fontSize: '22px', fontWeight: '600', color: '#22272D', marginBottom: '4px', margin: 0 }}>
            Activity Log
          </h1>
          <p style={{ fontSize: '13px', color: '#AAB0B8', marginBottom: 0 }}>Track all actions across the system</p>
        </div>
        {isSuper && activity.length > 0 && (
          <button type="button" onClick={() => setClearModal(true)} style={{
            padding: '8px 16px', borderRadius: '8px', border: '0.5px solid #FF766B',
            background: 'transparent', color: '#FF766B', fontSize: '13px',
            fontWeight: '600', cursor: 'pointer', fontFamily: 'Manrope, sans-serif'
          }}>
            Clear log
          </button>
        )}
      </div>

      {activity.length === 0 ? (
        <div style={{ background: '#fff', border: '0.5px solid #AAB0B8', borderRadius: '12px', padding: '60px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
          <div style={{ fontSize: '14px', color: '#AAB0B8', fontFamily: 'Manrope, sans-serif' }}>No activity yet</div>
        </div>
      ) : (
        <div style={{ background: '#fff', border: '0.5px solid #AAB0B8', borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '70vh' }}>
          {/* Scrollable activity list */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '0' }}>
            {activity.map((item, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '14px',
                  padding: '16px 20px',
                  borderBottom: i < activity.length - 1 ? '0.5px solid #F4FAFF' : 'none',
                }}
              >
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: '#22272D',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#F1FF5C',
                    fontSize: '13px',
                    fontWeight: '600',
                    flexShrink: 0,
                    fontFamily: 'Manrope, sans-serif',
                  }}
                >
                  {item.user_name.charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '14px', color: '#22272D', fontWeight: '500', fontFamily: 'Manrope, sans-serif' }}>
                    {item.user_name}
                    <span style={{ fontWeight: '400', color: '#AAB0B8', marginLeft: '6px' }}>
                      {item.action}
                    </span>
                  </div>
                  <div style={{ fontSize: '13px', color: '#AAB0B8', fontFamily: 'Manrope, sans-serif', marginTop: '2px', wordBreak: 'break-word' }}>
                    {item.document}
                  </div>
                  <div style={{ fontSize: '12px', color: '#3B9EBF', marginTop: '4px', fontFamily: 'Manrope, sans-serif' }}>
                    {timeAgo(item.timestamp)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Clear confirmation modal */}
      {clearModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: '#fff', borderRadius: '16px', padding: '32px',
            width: '420px', border: '0.5px solid #AAB0B8'
          }}>
            <h2 style={{
              fontFamily: 'Sora, sans-serif', fontSize: '18px',
              fontWeight: '600', color: '#22272D', marginBottom: '8px'
            }}>
              Clear activity log
            </h2>
            <p style={{ fontSize: '13px', color: '#AAB0B8', marginBottom: '20px' }}>
              Are you sure you want to permanently delete all activity records? This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" onClick={handleClearConfirm} disabled={clearing} style={{
                flex: 1, padding: '10px', borderRadius: '8px', border: 'none',
                background: '#FF766B', color: '#fff', fontSize: '14px',
                fontWeight: '600', fontFamily: 'Manrope, sans-serif',
                cursor: clearing ? 'not-allowed' : 'pointer', opacity: clearing ? 0.7 : 1
              }}>
                {clearing ? 'Clearing…' : 'Yes, clear all'}
              </button>
              <button type="button" onClick={() => setClearModal(false)} disabled={clearing} style={{
                flex: 1, padding: '10px', borderRadius: '8px',
                border: '0.5px solid #AAB0B8', background: 'transparent',
                color: '#22272D', fontSize: '14px', fontFamily: 'Manrope, sans-serif',
                cursor: clearing ? 'not-allowed' : 'pointer'
              }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ActivityLog;